import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, Image, RefreshControl } from 'react-native';
import SwipeableTabs, { SwipeableTabsRef } from '@/components/SwipeableTabs';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, withSequence, withTiming, withRepeat, cancelAnimation, Easing } from 'react-native-reanimated';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Haptics from 'expo-haptics';
import { useBill } from '@/contexts/BillContext';
import { useNotes } from '@/contexts/NotesContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSentimento } from '@/contexts/SentimentoContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSobriety } from '@/contexts/SobrietyContext';
import FilterDrawer from '@/components/FilterDrawer';
import ActionBottomSheet from '@/components/ActionBottomSheet';
import FormattedText from '@/components/FormattedText';
import AuthorFilterContent from '@/components/AuthorFilterContent';
import PeriodFilterContent from '@/components/PeriodFilterContent';
import SentimentFilterContent from '@/components/SentimentFilterContent';
import Toast from '@/components/Toast';
import CheckinRegister from '@/components/CheckinRegister';
import RelapseRegister from '@/components/RelapseRegister';
import BillBottomNav from '@/components/BillBottomNav';
import { SkeletonPost, SkeletonMissoes } from '@/components/Skeleton';
import SobrietyClockFull from '@/components/SobrietyClockFull';
import SentimentCalendar from '@/components/SentimentCalendar';
import NoteAvatar from '@/components/NoteAvatar';
import Avatar from '@/components/Avatar';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import { CheckinService, type CheckinHistory } from '@/services/checkinService';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import SearchOverlay from '@/components/SearchOverlay';
import MissoesGoalCard from '@/components/MissoesGoalCard';
import EmptyState from '@/components/EmptyState';
import FlagIcon from '@/components/icons/FlagIcon';
import CurrentStageSheet from '@/components/CurrentStageSheet';
import EncontrosWeekStrip from '@/components/EncontrosWeekStrip';
import MissionRow from '@/components/MissionRow';
import MissionHistoryRow from '@/components/MissionHistoryRow';
import JourneyOriginMarker from '@/components/JourneyOriginMarker';
import { recoveryPlanService, type RecoveryTodayResponse, type MissionKey, MISSION_KEYS } from '@/services/recoveryPlanService';
import { setClarityPageName } from '@/utils/clarityEvents';

interface Note {
  id: string;
  type: 'user' | 'bill' | 'sentimento' | 'checkin' | 'historical-checkin' | 'trail' | 'relapse';
  content: string;
  timestamp: Date;
  emotion?: string;
  category?: string | undefined;
  meetingId?: string;
  daysSoberBefore?: number;
}

interface FilterState {
  search: string;
  author: 'all' | 'user' | 'bill';
  period: 'all' | '7days' | '15days' | '30days';
  sentiment: string;
}

export default function DiaryScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { getCurrentDays } = useSobriety();
  const { sentimentSaved, savedEmotion, noteSaved, noteUpdated, openAddNote } = useLocalSearchParams<{ sentimentSaved?: string; savedEmotion?: string; noteSaved?: string; noteUpdated?: string; openAddNote?: string }>();
  const { messages } = useBill();
  const { userNotes, savedBillMessages, deleteNote, isLoading, refreshEntries: refreshNotes, unsaveBillMessage } = useNotes();
  const { userProfile, user, session, sessionReady } = useAuth();
  const { entries: sentimentoEntries, deleteEntry, refreshEntries } = useSentimento();
  const { unreadCount } = useNotifications();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    author: 'all',
    period: 'all',
    sentiment: 'all'
  });

  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [currentStageOpen, setCurrentStageOpen] = useState(false);
  const [floatingMenuVisible, setFloatingMenuVisible] = useState(false);
  const [authorFilterVisible, setAuthorFilterVisible] = useState(false);
  const [periodFilterVisible, setPeriodFilterVisible] = useState(false);
  const [sentimentFilterVisible, setSentimentFilterVisible] = useState(false);
  const [tempAuthorFilter, setTempAuthorFilter] = useState<'all' | 'user' | 'bill'>('all');
  const [tempPeriodFilter, setTempPeriodFilter] = useState<'all' | '7days' | '15days' | '30days'>('all');
  const [tempSentimentFilter, setTempSentimentFilter] = useState<string>('all');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  // Historical check-ins state
  const [historicalCheckins, setHistoricalCheckins] = useState<CheckinHistory['checkins']>([]);
  const [historicalCheckinsLoading, setHistoricalCheckinsLoading] = useState(false);
  const [historicalCheckinsError, setHistoricalCheckinsError] = useState<string | null>(null);
  const [hiddenCheckins, setHiddenCheckins] = useState<Set<string>>(new Set());
  
  // Tab state — Missões is Page 0 and the default landing tab (PRD §7.4)
  const [activeTab, setActiveTab] = useState<'missoes' | 'relogio' | 'notas'>('missoes');
  const diarySwipeRef = useRef<SwipeableTabsRef>(null);
  const DIARY_TABS = ['missoes', 'relogio', 'notas'] as const;

  // Recovery Plan / Missões — fetch on mount + every focus + manual refresh
  const [missoesData, setMissoesData] = useState<RecoveryTodayResponse | null>(null);
  const [missoesLoading, setMissoesLoading] = useState(true);
  const [missoesRefreshing, setMissoesRefreshing] = useState(false);
  const [missoesError, setMissoesError] = useState<string | null>(null);

  // PRD §5.4 — Missions completion-detection state.
  //
  // The recovery-plan endpoint is read-only: missions are completed
  // implicitly when the user logs the underlying activity (sentimento,
  // trail step, community post, …) and the server flips `completed: true`
  // on the next `getToday()` payload. The canonical site for the
  // `missoes_completed` micro is therefore *here* — we diff the freshly
  // loaded mission set against the previously known one and fire one
  // event per `false → true` transition.
  //
  // The ref is `{dateKey, map}` so we can scope the baseline to the local
  // calendar day. Day-rollover while the app stays alive resets the
  // baseline (the next load's diff is skipped) — this avoids
  // misclassifying yesterday-vs-today transitions (e.g. a mission that
  // was `true` at 23:59 yesterday and `true` again at 00:01 today after
  // the server's daily reset would otherwise look like "no transition"
  // and miss a real completion fired on day 2).
  //
  // `null` ref means we have no baseline yet (cold-start, first load).
  // We do NOT fire on the first load OR on the first load after a date
  // change — pre-existing completed missions belong to an earlier
  // session/day, not to "the user just completed it now". Subsequent
  // loads within the same day compare against the baseline and fire
  // only on real transitions.
  const previousMissionsRef = useRef<
    { dateKey: string; map: Map<MissionKey, boolean> } | null
  >(null);

  const loadMissoes = async (force = false) => {
    try {
      setMissoesError(null);
      const data = await recoveryPlanService.getToday({ force });
      if (data?.ok) {
        setMissoesData(data);

        // PRD §5.4 — completion-detection diff. Local-date scoped so day
        // rollover resets the baseline. `missoes_first_completed` (macro,
        // idempotent via `fireFirstEverOnly`) fires for AT MOST ONE
        // mission per load cycle to avoid a `getItem`/`setItem` race in
        // the AsyncStorage flag when multiple missions flip in the same
        // loop. The macro itself stays semantically correct because
        // "first mission ever" is a single event by definition — the
        // first flipping mission in the loop is as good as any.
        const dateKey = new Date().toISOString().slice(0, 10);
        const nextMap = new Map<MissionKey, boolean>(
          data.missions.map((m) => [m.key, m.completed] as const),
        );
        const prev = previousMissionsRef.current;
        const sameDayBaseline = prev && prev.dateKey === dateKey;
        if (sameDayBaseline) {
          let firstEverPendingForLoop = true;
          for (const [key, completed] of nextMap.entries()) {
            const wasCompleted = prev!.map.get(key) ?? false;
            if (!wasCompleted && completed) {
              const claimFirstEver = firstEverPendingForLoop;
              firstEverPendingForLoop = false;
              import('@/utils/clarityEvents')
                .then(({ logMissoesCompleted, logMissoesFirstCompleted }) => {
                  logMissoesCompleted({ mission_id: key });
                  if (claimFirstEver) logMissoesFirstCompleted({ mission_id: key });
                })
                .catch(() => {});
            }
          }
        }
        previousMissionsRef.current = { dateKey, map: nextMap };
      } else setMissoesError('load_failed');
    } catch (e) {
      console.error('[diary] loadMissoes failed', e);
      setMissoesError('load_failed');
    } finally {
      setMissoesLoading(false);
      setMissoesRefreshing(false);
    }
  };

  const handleMissoesRefresh = () => {
    setMissoesRefreshing(true);
    // Drop the cache so we hit the live endpoint, picking up missions the
    // user just completed in another tab. (Activity-write hook wiring is
    // tracked as a follow-up — for now focus + manual refresh covers the
    // "I came back to Missões" case.)
    recoveryPlanService.invalidateToday();
    loadMissoes(true);
  };

  useEffect(() => {
    if (sessionReady && user) loadMissoes();
  }, [sessionReady, user]);

  // Refresh on screen focus so completing a mission elsewhere (sentiment,
  // check-in, community post, trail step, Bill chat) reflects when the
  // user returns to the diary tab. Cheap because of the 5-min cache —
  // the call short-circuits unless we force it.
  useFocusEffect(
    React.useCallback(() => {
      if (sessionReady && user) {
        recoveryPlanService.invalidateToday();
        loadMissoes(true);
      }
    }, [sessionReady, user])
  );

  const handleDiaryTabPress = (tab: 'missoes' | 'relogio' | 'notas') => {
    if (activeTab !== tab) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      setActiveTab(tab);
    }
  };

  // PRD §5.4 + REF dashboard §7.2 / §9.4 — per-tab Clarity signals.
  //
  // The route `/diary` resolves to a single page name (`Diary`) for all
  // three tabs, which collapsed every per-tab metric in the dashboard.
  // We override the page name per active tab AND fire `diary_tab_viewed`
  // (cross-tab entry signal) plus `missoes_viewed` (area-specific filter
  // row) on every tab change, including the first-mount default.
  const DIARY_TAB_PAGE_NAME: Record<'missoes' | 'relogio' | 'notas', string> = {
    missoes: 'Diary · Missões',
    relogio: 'Diary · Relógio',
    notas: 'Diary · Notas',
  };
  useEffect(() => {
    setClarityPageName(DIARY_TAB_PAGE_NAME[activeTab]).catch(() => {});
    import('@/utils/clarityEvents')
      .then(({ logDiaryTabViewed, logMissoesViewed }) => {
        logDiaryTabViewed({ tab: activeTab });
        if (activeTab === 'missoes') logMissoesViewed();
      })
      .catch(() => {});
  }, [activeTab]);

  // Pull to refresh state
  const [refreshing, setRefreshing] = useState(false);
  const rotation = useSharedValue(0);

  // Animated values for floating menu
  const sentimentScale = useSharedValue(0);
  const sentimentOpacity = useSharedValue(0);
  const noteScale = useSharedValue(0);
  const noteOpacity = useSharedValue(0);

  // No navigation override needed - use standard back behavior

  // Function to show toast with message and type
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // Pull to refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    
    // Start rotation animation
    rotation.value = withRepeat(
      withTiming(360, {
        duration: 1000,
        easing: Easing.linear,
      }),
      -1,
      false
    );

    try {
      // Refresh all diary data in parallel
      await Promise.all([
        refreshNotes(),
        refreshEntries()
      ]);
      
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      console.error('Error refreshing diary:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      cancelAnimation(rotation);
      rotation.value = withTiming(0, { duration: 200 });
      setRefreshing(false);
    }
  };

  // CRITICAL FIX: Load notes and sentimentos when session becomes ready
  // This ensures data loads even if the context's useEffect fired before sessionReady was true
  // C3 (PV2-16): session?.access_token rotates on auto-refresh; gate on
  // sessionReady only — apiService reads the fresh token internally.
  useEffect(() => {
    if (user && sessionReady && session?.access_token) {
      console.log('📝 DiaryScreen: Session ready, refreshing notes and sentimentos...');
      refreshNotes();
      refreshEntries();
    }
  }, [user, sessionReady]);

  // Load hidden check-ins from AsyncStorage on component mount
  useEffect(() => {
    const loadHiddenCheckins = async () => {
      try {
        const stored = await AsyncStorage.getItem('hiddenCheckins');
        if (stored) {
          setHiddenCheckins(new Set(JSON.parse(stored)));
        }
      } catch (error) {
        console.error('Error loading hidden check-ins:', error);
      }
    };

    loadHiddenCheckins();
  }, []);

  // Save hidden check-ins to AsyncStorage whenever the set changes
  const updateHiddenCheckins = async (newHiddenCheckins: Set<string>) => {
    try {
      setHiddenCheckins(newHiddenCheckins);
      await AsyncStorage.setItem('hiddenCheckins', JSON.stringify([...newHiddenCheckins]));
    } catch (error) {
      console.error('Error saving hidden check-ins:', error);
    }
  };

  // Show toast when sentiment is saved or note is created/updated
  useEffect(() => {
    let shouldShowToast = false;
    let message = '';

    if (sentimentSaved === 'true') {
      shouldShowToast = true;
      // Create a personalized message with the emotion if available
      if (savedEmotion) {
        const emotionEmojiMap: { [key: string]: string } = {
          'feliz': '😀',
          'animado': '🤩',
          'tranquilo': '😌',
          'cansado': '😴',
          'frustrado': '😣',
          'triste': '😭',
          'estressado': '😤',
          'irritado': '😠',
          'ansioso': '😰',
        };
        const emotionText = t(`diary.emotions.${savedEmotion}`);
        const emotionEmoji = emotionEmojiMap[savedEmotion] || '';
        message = `${t('diary.filters.sentiment')} "${emotionText} ${emotionEmoji}" ${t('diary.sentiment.savedSuccess')}`;
      } else {
        message = t('diary.sentiment.savedSuccess');
      }
      
      // Refresh sentiment entries to show the new entry
      refreshEntries();
    } else if (noteSaved === 'true') {
      shouldShowToast = true;
      message = t('diary.sentiment.noteSavedSuccess');
      
      // Refresh notes to show the new note
      refreshNotes();
    } else if (noteUpdated === 'true') {
      shouldShowToast = true;
      message = t('diary.sentiment.noteUpdatedSuccess');
      
      // Refresh notes to show the updated note
      refreshNotes();
    }

    if (shouldShowToast) {
      showToast(message, 'success');

      // Clear the parameter after a small delay to avoid showing toast again
      setTimeout(() => {
        router.replace('/diary');
      }, 100);
    }
  }, [sentimentSaved, savedEmotion, noteSaved, noteUpdated, router, refreshEntries, refreshNotes]);

  // Auto-redirect to add-note when openAddNote param is set (from onboarding)
  useEffect(() => {
    if (openAddNote === 'true') {
      router.replace({
        pathname: '/add-note',
        params: { returnTo: 'diary' }
      });
    }
  }, [openAddNote, router]);

  // Fetch historical check-ins when component mounts
  // Uses API endpoint to avoid Supabase client timing issues (all-API architecture)
  useEffect(() => {
    const fetchHistoricalCheckins = async () => {
      setHistoricalCheckinsLoading(true);
      setHistoricalCheckinsError(null);
      
      try {
        console.log('📋 DiaryScreen: Loading historical check-ins via API...');
        const result = await CheckinService.getCheckinHistoryViaAPI(100);
        
        if (result.success) {
          setHistoricalCheckins(result.checkins);
          console.log('📋 DiaryScreen: Loaded', result.checkins.length, 'historical check-ins');
        } else {
          console.log('ℹ️ Check-ins not available');
          setHistoricalCheckins([]);
        }
      } catch (error) {
        console.log('ℹ️ Could not load check-ins:', error);
        setHistoricalCheckins([]);
      } finally {
        setHistoricalCheckinsLoading(false);
      }
    };

    // Only fetch if user is authenticated AND session is ready
    // C3 (PV2-16): drop access_token from deps — Supabase rotates it; the
    // boolean sessionReady is the stable gate.
    if (user && sessionReady && session?.access_token) {
      fetchHistoricalCheckins();
    }
  }, [user, sessionReady]);

  // Combine user notes, Bill messages, and sentimento entries into a single timeline
  const getAllNotes = (): Note[] => {
    // Safely handle messages array - get from BillContext
    const safeMessages = Array.isArray(messages) ? messages : [];

    const billMessages: Note[] = safeMessages
      .filter(msg => msg && msg.role === 'assistant' && savedBillMessages.has(msg.id))
      .map((msg) => ({
        id: `bill-${msg.id}`,
        type: 'bill' as const,
        content: msg.content || '',
        timestamp: new Date(msg.timestamp || new Date()),
      }));

    // Get IDs of notes that are linked to sentiment entries to avoid duplicates
    const sentimentNoteIds = new Set(
      sentimentoEntries
        .filter(entry => entry.note?.id)
        .map(entry => entry.note!.id)
    );

    // Filter out user notes that are already part of sentiment entries
    const formattedUserNotes: Note[] = userNotes
      .filter(note => !sentimentNoteIds.has(note.id))
      .map((note) => {
        // Use type from database, fallback to content detection for legacy notes
        let noteType: 'user' | 'checkin' | 'trail' | 'relapse' = 'user';
        
        if (note.type === 'checkin') {
          noteType = 'checkin';
        } else if (note.type === 'trail') {
          noteType = 'trail';
        } else if (note.type === 'relapse') {
          noteType = 'relapse';
        } else if (note.content.startsWith('Check-in 📍')) {
          // Fallback for legacy notes without type field
          noteType = 'checkin';
        }
        
        return {
          id: `user-${note.id}`,
          type: noteType,
          content: note.content,
          timestamp: note.timestamp,
        };
      });

    const formattedSentimentoEntries: Note[] = sentimentoEntries.map((entry) => {
      // List of trail step names
      const trailStepNames = [
        'Comece', 'Acredite', 'Confie', 'Racionalize', 'Assimile', 'Mude',
        'Humildade', 'Reflexão', 'Repare', 'Autoanálise', 'Conexão', 'Comprometimento',
        'Trilha' // fallback
      ];

      // Check if this is a trail completion entry
      const isTrailCompletion = entry.category === 'sobriedade' && 
                                 trailStepNames.includes(entry.emotion);

      if (isTrailCompletion && entry.note?.content) {
        return {
          id: `trail-${entry.id}`,
          type: 'trail' as const,
          content: entry.note.content,
          timestamp: new Date(entry.created_at),
          emotion: entry.emotion,
          category: entry.category,
        };
      }

      const getEmotionEmoji = (emotionId: string) => {
        const emotionMap: { [key: string]: string } = {
          'feliz': '😀',
          'animado': '🤩',
          'tranquilo': '😌',
          'cansado': '😴',
          'frustrado': '😣',
          'triste': '😭',
          'estressado': '😤',
          'irritado': '😠',
          'ansioso': '😰',
        };
        return emotionMap[emotionId] || '😐';
      };

      const getCategoryLabel = (categoryValue: string) => {
          if (!categoryValue) return '';

          // Handle comma-separated categories
          const categories = categoryValue.split(',').map(c => c.trim());
          const labels = categories.map(id => t(`diary.categories.${id}`) || id).filter(Boolean);

          if (labels.length === 0) return '';
          if (labels.length === 1) return labels[0];
          if (labels.length === 2) return `${labels[0]} ${t('diary.categories.and')} ${labels[1]}`;

          const lastLabel = labels.pop();
          return `${labels.join(', ')} ${t('diary.categories.and')} ${lastLabel}`;
        };

        const categoryLabel = entry.category ? getCategoryLabel(entry.category) : '';
        const emotionLabel = t(`diary.emotions.${entry.emotion}`) || entry.emotion.charAt(0).toUpperCase() + entry.emotion.slice(1);
        let content = `${getEmotionEmoji(entry.emotion)} ${t('diary.sentimentLabel')}: ${emotionLabel}`;
        if (categoryLabel) {
          content += `\n${t('diary.contextLabel')}: ${categoryLabel}`;
        }
        if (entry.note?.content) {
          content += `\n\n${entry.note.content}`;
        }

      return {
        id: `sentimento-${entry.id}`,
        type: 'sentimento' as const,
        content,
        timestamp: new Date(entry.created_at),
        emotion: entry.emotion,
        category: entry.category,
      };
    });

    // Format historical check-ins and filter out hidden ones
    const formattedHistoricalCheckins: Note[] = historicalCheckins
      .filter(checkin => !hiddenCheckins.has(checkin.id))
      .map((checkin) => {
        const meetingName = checkin.meetings?.meeting_name || t('meetings.meeting');
        const checkinTime = new Date(checkin.checked_in_at);
        // Use locale-aware time formatting based on user's language
        const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
        const timeString = checkinTime.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        const content = t('diary.checkinText', { meetingName, time: timeString });
        
        return {
          id: `historical-checkin-${checkin.id}`,
          type: 'historical-checkin' as const,
          content,
          timestamp: checkinTime,
          meetingId: checkin.meeting_id,
        };
      });

    // Combine all notes (relapses are now loaded from notes table with type='relapse')
    const allNotes = [...billMessages, ...formattedUserNotes, ...formattedSentimentoEntries, ...formattedHistoricalCheckins];

    // Deduplication logic: Remove duplicates where same check-in exists as both user note and historical check-in
    const deduplicatedNotes = allNotes.reduce((acc: Note[], note) => {
      if ((note.type === 'checkin' || note.type === 'historical-checkin') && note.meetingId) {
        // Use meetingId and date for locale-independent deduplication
        const noteDate = new Date(note.timestamp).toDateString();
        
        // Check if there's already a check-in for the same meeting and date
        const duplicateIndex = acc.findIndex(existingNote => {
          if (existingNote.type !== 'checkin' && existingNote.type !== 'historical-checkin') {
            return false;
          }
          
          const existingDate = new Date(existingNote.timestamp).toDateString();
          
          // Compare meeting ID and date
          return existingNote.meetingId === note.meetingId && noteDate === existingDate;
        });
        
        if (duplicateIndex !== -1) {
          // If there's a duplicate, prefer the user note (checkin) over historical check-in
          if (note.type === 'checkin') {
            acc[duplicateIndex] = note; // Replace historical with user note
          }
          // If current note is historical-checkin and existing is already checkin, skip this one
          return acc;
        }
      }
      
      acc.push(note);
      return acc;
    }, []);

    return deduplicatedNotes.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  const getFilteredNotes = (): Note[] => {
    const allNotes = getAllNotes();
    const now = new Date();

    return allNotes.filter((note) => {
      // Search filter
      if (filters.search.trim() && !note.content.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }

      // Author filter - treat sentimento, checkin, and historical-checkin as user content
      if (filters.author !== 'all') {
        if (filters.author === 'user' && note.type !== 'user' && note.type !== 'sentimento' && note.type !== 'checkin' && note.type !== 'historical-checkin' && note.type !== 'relapse') {
          return false;
        }
        if (filters.author === 'bill' && note.type !== 'bill') {
          return false;
        }
      }

      // Period filter
      if (filters.period !== 'all') {
        const daysDiff = Math.floor((now.getTime() - note.timestamp.getTime()) / (1000 * 60 * 60 * 24));
        const periodDays = {
          '7days': 7,
          '15days': 15,
          '30days': 30
        };

        if (daysDiff > periodDays[filters.period]) {
          return false;
        }
      }

      // Sentiment filter - exclusive: only show sentimento notes matching the selected emotions
      if (filters.sentiment !== 'all') {
        if (note.type !== 'sentimento') {
          return false;
        }
        const selectedSentiments = filters.sentiment.split(',').filter(Boolean);
        if (!note.emotion || !selectedSentiments.includes(note.emotion)) {
          return false;
        }
      }

      return true;
    });
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const noteDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());

    if (noteDate.getTime() === today.getTime()) {
      return t('diary.today');
    } else if (noteDate.getTime() === yesterday.getTime()) {
      return t('diary.yesterday');
    } else {
      // Use locale-aware date formatting based on user's language
      const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
      return timestamp.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short'
      });
    }
  };

  const handleMenuPress = () => {
    router.push('/perfil');
  };

  const handleNotificationPress = () => {
    router.push('/notifications');
  };

  const handleAddNote = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (!floatingMenuVisible) {
      // Reset animation values before showing
      noteOpacity.value = 0;
      noteScale.value = 0.8;
      sentimentOpacity.value = 0;
      sentimentScale.value = 0.8;

      setFloatingMenuVisible(true);

      // Animate note button first (from bottom) - faster animation
      noteOpacity.value = withTiming(1, { duration: 150 });
      noteScale.value = withSequence(
        withSpring(1.1, { damping: 20, stiffness: 150 }),
        withSpring(1, { damping: 25, stiffness: 180 })
      );

      // Animate sentiment button with smaller delay (appears after note button)
      setTimeout(() => {
        sentimentOpacity.value = withTiming(1, { duration: 150 });
        sentimentScale.value = withSequence(
          withSpring(1.1, { damping: 20, stiffness: 150 }),
          withSpring(1, { damping: 25, stiffness: 180 })
        );
      }, 60);
    } else {
      // Hide with faster reverse animation
      sentimentOpacity.value = withTiming(0, { duration: 120 });
      sentimentScale.value = withTiming(0.8, { duration: 120 });
      noteOpacity.value = withTiming(0, { duration: 120 });
      noteScale.value = withTiming(0.8, { duration: 120 });

      setTimeout(() => {
        setFloatingMenuVisible(false);
      }, 120);
    }
  };

  const handleNoteMenuPress = (note: Note) => {
    setSelectedNote(note);
    setActionMenuVisible(true);
  };

  const handleEditNote = () => {
    // Explicit safeguard: Block editing of check-in notes, historical check-ins, and relapses
    if (selectedNote && (selectedNote.type === 'checkin' || selectedNote.type === 'historical-checkin' || selectedNote.type === 'relapse')) {
      console.warn('Attempted to edit a non-editable entry - this operation is not allowed');
      showToast(t('diary.sentiment.cannotEdit'), 'error');
      setActionMenuVisible(false);
      return;
    }

    if (selectedNote && selectedNote.type === 'user') {
      const noteId = selectedNote.id.replace('user-', '');
      router.push({
        pathname: '/add-note',
        params: { 
          editNoteId: noteId,
          noteContent: selectedNote.content,
          returnTo: 'diary'
        }
      });
    } else if (selectedNote && selectedNote.type === 'sentimento') {
      // For sentiment notes, we allow editing the text content
      const entryId = selectedNote.id.replace('sentimento-', '');
      const sentimentoEntry = sentimentoEntries.find(entry => entry.id === entryId);

      if (sentimentoEntry) {
        // Always use the actual note content from the database for editing
        const editContent = sentimentoEntry.note?.content || '';

        router.push({
          pathname: '/add-note',
          params: { 
            editSentimentoId: entryId,
            editNoteId: sentimentoEntry.note?.id || '',
            noteContent: editContent,
            returnTo: 'diary'
          }
        });
      }
    }
    setActionMenuVisible(false);
  };

  const handleDeleteNote = async () => {
    try {
      if (selectedNote && selectedNote.type === 'user') {
        const noteId = selectedNote.id.replace('user-', '');
        await deleteNote(noteId);
        showToast(t('diary.noteDeleted'), 'success');
      } else if (selectedNote && selectedNote.type === 'checkin') {
        const noteId = selectedNote.id.replace('user-', '');
        await deleteNote(noteId);
        showToast(t('diary.checkinRemoved'), 'success');
      } else if (selectedNote && selectedNote.type === 'historical-checkin') {
        // For historical check-ins, implement hide functionality
        const checkinId = selectedNote.id.replace('historical-checkin-', '');
        const newHiddenCheckins = new Set(hiddenCheckins);
        newHiddenCheckins.add(checkinId);
        await updateHiddenCheckins(newHiddenCheckins);
        showToast(t('diary.checkinHidden'), 'success');
      } else if (selectedNote && selectedNote.type === 'sentimento') {
        const entryId = selectedNote.id.replace('sentimento-', '');
        const success = await deleteEntry(entryId);
        if (!success) {
          console.error('Failed to delete sentimento entry:', entryId);
          showToast(t('diary.sentiment.error'), 'error');
        } else {
          // Refresh both sentiment entries and notes contexts to ensure UI is fully updated
          await Promise.all([
            refreshEntries(),
            refreshNotes()
          ]);
          showToast(t('diary.sentiment.deleted'), 'success');
        }
      } else if (selectedNote && selectedNote.type === 'relapse') {
        // For relapse entries, delete from notes table
        const noteId = selectedNote.id.replace('user-', '');
        await deleteNote(noteId);
        showToast(t('diary.relapseDeleted') || t('diary.noteDeleted'), 'success');
      }
    } catch (error) {
      console.error('Error deleting note/sentiment:', error);
      showToast(t('diary.deleteError'), 'error');
    }
    setActionMenuVisible(false);
  };

  const handleRemoveFavorite = () => {
    try {
      if (selectedNote && selectedNote.type === 'bill') {
        const billMessageId = selectedNote.id.replace('bill-', '');
        // Remove the bill message from saved messages
        unsaveBillMessage(billMessageId);
        showToast(t('diary.messageRemoved'), 'success');
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
      showToast(t('diary.removeMessageError'), 'error');
    }
    setActionMenuVisible(false);
  };

  const handleActionMenuClose = () => {
    setActionMenuVisible(false);
  };

  const handleAuthorFilterPress = () => {
    setTempAuthorFilter(filters.author);
    setAuthorFilterVisible(true);
  };

  const handlePeriodFilterPress = () => {
    setTempPeriodFilter(filters.period);
    setPeriodFilterVisible(true);
  };

  const handleApplyAuthorFilter = () => {
    setFilters(prev => ({ ...prev, author: tempAuthorFilter }));
    setAuthorFilterVisible(false);
  };

  const handleApplyPeriodFilter = () => {
    setFilters(prev => ({ ...prev, period: tempPeriodFilter }));
    setPeriodFilterVisible(false);
  };

  const handleClearAuthorFilter = () => {
    setFilters(prev => ({ ...prev, author: 'all' }));
    setTempAuthorFilter('all');
    setAuthorFilterVisible(false);
  };

  const handleClearPeriodFilter = () => {
    setFilters(prev => ({ ...prev, period: 'all' }));
    setTempPeriodFilter('all');
    setPeriodFilterVisible(false);
  };

  const handleSentimentFilterPress = () => {
    setTempSentimentFilter(filters.sentiment);
    setSentimentFilterVisible(true);
  };

  const handleApplySentimentFilter = () => {
    setFilters(prev => ({ ...prev, sentiment: tempSentimentFilter }));
    setSentimentFilterVisible(false);
  };

  const handleClearSentimentFilter = () => {
    setFilters(prev => ({ ...prev, sentiment: 'all' }));
    setTempSentimentFilter('all');
    setSentimentFilterVisible(false);
  };

  const handleSearchPress = () => {
    setIsSearchActive(true);
  };

  const handleSearchCancel = () => {
    setFilters(prev => ({ ...prev, search: '' }));
    setIsSearchActive(false);
  };

  const handleSearchSubmit = () => {
    if (filters.search.trim() && !recentSearches.includes(filters.search.trim())) {
      setRecentSearches([filters.search.trim(), ...recentSearches.slice(0, 4)]);
    }
  };

  const handleRecentSearchPress = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
  };

  const filteredNotes = getFilteredNotes();


  // Animated styles for floating menu buttons
  const animatedSentimentStyle = useAnimatedStyle(() => ({
    opacity: sentimentOpacity.value,
    transform: [{ scale: sentimentScale.value }],
  }));

  const animatedNoteStyle = useAnimatedStyle(() => ({
    opacity: noteOpacity.value,
    transform: [{ scale: noteScale.value }],
  }));



  const NoteCard = ({ note, isLast }: { note: Note; isLast: boolean }) => {
    const getEmotionEmoji = (emotionId: string) => {
      const emotionMap: { [key: string]: string } = {
        'feliz': '😀',
        'animado': '🤩',
        'tranquilo': '😌',
        'cansado': '😴',
        'frustrado': '😣',
        'triste': '😭',
        'estressado': '😤',
        'irritado': '😠',
        'ansioso': '😰',
      };
      return emotionMap[emotionId] || '😐';
    };

    const renderSentimentoContent = () => {
      if (note.type !== 'sentimento') return null;

      // Check if it's a default sentiment note without user content
      const hasUserNote = note.content.includes('\n\n');
      const lines = note.content.split('\n');
      const contextLine = lines.find(line => line.includes('Contexto:')); // "Contexto: Trabalho"
      const userContent = hasUserNote ? lines.slice(contextLine ? 2 : 1).join('\n').trim() : null;

      // If no user content, create default message
      if (!userContent) {
        const emotion = note.emotion || 'neutro';
        const category = note.category;
        const emotionLabel = t(`diary.emotions.${emotion}`) || emotion.charAt(0).toUpperCase() + emotion.slice(1);
        const getCategoryLabel = (categoryValue: string) => {
          if (!categoryValue) return '';

          // Handle comma-separated categories
          const categories = categoryValue.split(',').map(c => c.trim());
          const labels = categories.map(id => t(`diary.categories.${id}`) || id).filter(Boolean);

          if (labels.length === 0) return '';
          if (labels.length === 1) return labels[0];
          if (labels.length === 2) return `${labels[0]} ${t('diary.categories.and')} ${labels[1]}`;

          const lastLabel = labels.pop();
          return `${labels.join(', ')} ${t('diary.categories.and')} ${lastLabel}`;
        };

        const categoryLabel = category ? getCategoryLabel(category) : '';
        const defaultContent = categoryLabel 
          ? t('diary.sentiment.placeholderWithCategory', { emotion: emotionLabel, category: categoryLabel })
          : t('diary.sentiment.placeholderNoCategory', { emotion: emotionLabel });

        return defaultContent;
      }

      return userContent;
    };

    // Special handling for check-in notes and historical check-ins
    if (note.type === 'checkin' || note.type === 'historical-checkin') {
      return (
        <CheckinRegister
          content={note.content}
          timestamp={note.timestamp}
          isLast={isLast}
          onMenuPress={() => handleNoteMenuPress(note)}
        />
      );
    }

    // Special handling for relapse entries
    if (note.type === 'relapse') {
      return (
        <RelapseRegister
          daysSoberBefore={note.daysSoberBefore || 0}
          timestamp={note.timestamp}
          isLast={isLast}
          onMenuPress={() => handleNoteMenuPress(note)}
        />
      );
    }

    return (
      <View style={[styles.noteCardContainer, !isLast && styles.noteCardWithDivider]}>
        <View style={styles.noteAvatarColumn}>
          {note.type === 'bill' ? (
            <NoteAvatar type="bill" size={32} />
          ) : note.type === 'trail' ? (
            <NoteAvatar type="trail" size={32} />
          ) : note.type === 'sentimento' ? (
            <NoteAvatar type="sentimento" size={32} />
          ) : (
            <NoteAvatar type="anotacao" size={32} />
          )}
        </View>
        <View style={styles.userContent}>
          <View style={styles.userHeader}>
            <View style={styles.headerInfo}>
              <Text style={styles.authorName}>
                {note.type === 'bill' ? 'Bill' :
                 note.type === 'trail' ? (
                  note.content.includes('\n') ? note.content.split('\n')[0] : t('trail.title')
                 ) :
                 note.type === 'sentimento' && note.emotion ? (
                  `${t(`diary.emotions.${note.emotion}`) || note.emotion} ${getEmotionEmoji(note.emotion)}`
                ) : 'Anotação'}
              </Text>
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.timestamp}>
                {formatTimestamp(note.timestamp)}
              </Text>
              <TouchableOpacity
                style={styles.noteMenuButton}
                onPress={() => handleNoteMenuPress(note)}
              >
                <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <Path d="M10 5.5C10.8284 5.5 11.5 4.82843 11.5 4C11.5 3.17157 10.8284 2.5 10 2.5C9.17157 2.5 8.5 3.17157 8.5 4C8.5 4.82843 9.17157 5.5 10 5.5Z" fill="#757575"/>
                  <Path d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z" fill="#757575"/>
                  <Path d="M10 17.5C10.8284 17.5 11.5 16.8284 11.5 16C11.5 15.1716 10.8284 14.5 10 14.5C9.17157 14.5 8.5 15.1716 8.5 16C8.5 16.8284 9.17157 17.5 10 17.5Z" fill="#757575"/>
                </Svg>
              </TouchableOpacity>
            </View>
          </View>
          <FormattedText
            content={
              note.type === 'sentimento' ? (renderSentimentoContent() || '') :
              (note.type === 'trail' && note.content.includes('\n'))
                ? note.content.split('\n').slice(1).join('\n')
                : note.content
            }
            style={styles.noteContent}
          />
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Top Header - Fixed */}
      <View style={styles.topHeader}>
        <TouchableOpacity 
          style={styles.avatarButton}
          onPress={handleMenuPress}
        >
          {hasPadrinhoPlus ? (
            <AvatarBlurRing>
              <Avatar
                uri={userProfile?.avatar_url}
                size={32}
                showInitials={true}
                initials={userProfile?.full_name || undefined}
                borderWidth={0}
              />
            </AvatarBlurRing>
          ) : (
            <Avatar
              uri={userProfile?.avatar_url}
              size={32}
              showInitials={true}
              initials={userProfile?.full_name || undefined}
              borderColor="#7E7D75"
              borderWidth={1}
            />
          )}
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('diary.headerTitle')}</Text>
        <TouchableOpacity style={styles.notificationButton} onPress={handleNotificationPress}>
          <View style={styles.notificationIconContainer}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
            {unreadCount > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Pill Tab Bar + Search */}
      <View style={styles.pillTabContainer}>
        <View style={styles.pillTabBar}>
          <TouchableOpacity
            style={[styles.pillTab, activeTab === 'missoes' && styles.pillTabActive]}
            onPress={() => handleDiaryTabPress('missoes')}
            disabled={isSearchActive}
            activeOpacity={1}
          >
            <Text style={[styles.pillTabText, activeTab === 'missoes' && styles.pillTabTextActive]} numberOfLines={1}>
              {t('diary.tabs.missoes')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pillTab, activeTab === 'relogio' && styles.pillTabActive]}
            onPress={() => handleDiaryTabPress('relogio')}
            disabled={isSearchActive}
            activeOpacity={1}
          >
            <Text style={[styles.pillTabText, activeTab === 'relogio' && styles.pillTabTextActive]} numberOfLines={1}>
              {t('diary.tabs.relogio')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pillTab, activeTab === 'notas' && styles.pillTabActive]}
            onPress={() => handleDiaryTabPress('notas')}
            disabled={isSearchActive}
            activeOpacity={1}
          >
            <Text style={[styles.pillTabText, activeTab === 'notas' && styles.pillTabTextActive]} numberOfLines={1}>
              {t('diary.tabs.notas')}
            </Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={styles.tabSearchButton}
          onPress={handleSearchPress}
          disabled={isSearchActive}
        >
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path fillRule="evenodd" clipRule="evenodd" d="M11.6324 3C16.2814 3 20.0499 6.76847 20.0499 11.4175C20.0499 16.0666 16.2814 19.836 11.6324 19.836C6.98331 19.836 3.21484 16.0666 3.21484 11.4175C3.21484 6.76847 6.98331 3 11.6324 3Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M17.3594 17.584L20.7844 21.0002" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
        </TouchableOpacity>
      </View>

      {/* Swipeable Content */}
      <SwipeableTabs
        ref={diarySwipeRef}
        activeIndex={DIARY_TABS.indexOf(activeTab)}
        onPageSelected={(i) => {
          const tab = DIARY_TABS[i];
          if (tab && tab !== activeTab) {
            Haptics.selectionAsync();
            setActiveTab(tab);
          }
        }}
      >
        {/* Page 0: Missões — recovery journey home (PRD §7.4) */}
        <Animated.ScrollView
          key="missoes"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.missoesScrollContent}
          refreshControl={
            <RefreshControl
              refreshing={missoesRefreshing}
              onRefresh={handleMissoesRefresh}
              tintColor="#669AB7"
              colors={['#669AB7']}
            />
          }
        >
          {missoesLoading && !missoesData ? (
            <SkeletonMissoes />
          ) : missoesError && !missoesData ? (
            // Standard empty-state pattern — see EmptyState component.
            // 'top' variant because we live inside the missoes scroll
            // view and there's a sticky tab bar above us.
            <EmptyState
              variant="top"
              icon={<FlagIcon color="#151515" size={16} />}
              title={t('missoes.errors.loadFailed')}
              action={{
                label: t('missoes.errors.retry'),
                onPress: () => { setMissoesLoading(true); loadMissoes(true); },
              }}
            />
          ) : missoesData ? (
            <>
              {/* Greeting + current stage chip — sits above the goal card.
                  Mirrors profile.tsx greeting style; chip opens
                  CurrentStageSheet (Figma 6701:64800). */}
              {(() => {
                const firstName = userProfile?.full_name?.split(' ')[0]
                  || (t('profile.defaults.userName') as string);
                const stageNum = missoesData.stage.number;
                const stageName = t(`recoveryStages.${stageNum}.name`) as string;
                const helloRaw = (t('profile.greeting.hello') as string) || '';
                // Hello key is "Olá, " — drop the trailing comma/space so the
                // first line ends cleanly with a comma per Figma 6701:64800.
                const helloLine = helloRaw.replace(/[\s,]+$/, '') + ',';
                const waveRaw = (t('profile.greeting.wave') as string) || '';
                return (
                  <View style={styles.missoesGreetingRow}>
                    <View style={styles.missoesGreetingTextWrap}>
                      <Text style={styles.missoesGreetingHello}>
                        {helloLine}
                      </Text>
                      <Text
                        style={styles.missoesGreetingText}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {firstName}{waveRaw}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={styles.missoesStageChip}
                      onPress={() => setCurrentStageOpen(true)}
                      activeOpacity={0.7}
                    >
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path d="M7.83984 1.84863V13.6887" stroke="#015480" strokeWidth="1.5" strokeLinecap="square" />
                        <Path d="M13.7599 7.76855H1.91992" stroke="#015480" strokeWidth="1.5" strokeLinecap="square" />
                        <Path d="M12.0264 3.58301L3.6543 11.9551" stroke="#015480" strokeWidth="1.5" strokeLinecap="square" />
                        <Path d="M12.0264 11.9547L3.6543 3.58252" stroke="#015480" strokeWidth="1.5" strokeLinecap="square" />
                      </Svg>
                      <Text style={styles.missoesStageChipText}>{stageName}</Text>
                    </TouchableOpacity>
                  </View>
                );
              })()}

              {/* Day count comes from the client single source of truth
                  (getCurrentDays) so Missões matches the clock / widget /
                  share / Bill. The server `sobrietyDays` is used ONLY as the
                  "is sobriety set up?" (null vs non-null) signal — its numeric
                  value is never rendered. See utils/sobrietyDays.ts. */}
              <MissoesGoalCard
                stageNumber={missoesData.stage.number}
                appGoal={missoesData.appGoal}
                sobrietyDays={
                  missoesData.sobrietyDays !== null ? getCurrentDays() : null
                }
                onSobrietyPress={() => {
                  if (missoesData.sobrietyDays !== null && missoesData.sobrietyDays >= 0) {
                    handleDiaryTabPress('relogio');
                  } else {
                    router.push('/sobriety-clock');
                  }
                }}
              />

              {/* Appointment / Professional card — available to all authenticated users */}
              <TouchableOpacity
                style={styles.appointmentCard}
                activeOpacity={0.7}
                onPress={() => {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  router.push('/sponsors');
                }}
              >
                <View style={styles.appointmentAvatar}>
                  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <Path d="M5.1411 4.42627H10.3645C12.1899 4.42627 13.3268 5.71529 13.3268 7.53904V12.4606C13.3268 14.2843 12.1899 15.5733 10.3637 15.5733H5.1411C3.31486 15.5733 2.17969 14.2843 2.17969 12.4606V7.53904C2.17969 5.71529 3.32068 4.42627 5.1411 4.42627Z" stroke="#3D3C38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M13.3223 8.45459L15.9426 6.30983C16.3668 5.96221 16.993 6.02459 17.3406 6.44872C17.4862 6.62668 17.566 6.84956 17.5652 7.07909L17.556 12.9262C17.5543 13.4751 17.1094 13.9192 16.5606 13.9175C16.3319 13.9175 16.1098 13.8377 15.9327 13.6921L13.3223 11.5482" stroke="#3D3C38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <Path fillRule="evenodd" clipRule="evenodd" d="M5.83984 10.0016C5.83984 11.0586 6.69641 11.9143 7.75261 11.9143C8.80954 11.9143 9.66615 11.0586 9.66615 10.0016C9.66615 8.94462 8.80954 8.08887 7.75261 8.08887C6.69641 8.08887 5.83984 8.94462 5.83984 10.0016Z" stroke="#3D3C38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                </View>
                <View style={styles.appointmentTextGroup}>
                  <Text style={styles.appointmentTitle}>{t('appointments.findProfessionals')}</Text>
                  <Text style={styles.appointmentSubtitle}>{t('appointments.newAppointment')}</Text>
                </View>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M5.83398 3.3335L10.5007 8.00016L5.83398 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
                </Svg>
              </TouchableOpacity>

              <EncontrosWeekStrip days={missoesData.encontros7d} />

              {/* Today header — avatar (dashed border + 4-burst star) +
                  formatted date. Sits ABOVE the timeline rail per Figma
                  6313:26670; not anchored to a rail dot. */}
              {(() => {
                const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
                const today = new Date();
                const formatted = today.toLocaleDateString(locale, {
                  day: '2-digit',
                  month: 'long',
                });
                const capitalized = locale === 'pt-BR'
                  ? formatted.replace(/de (\p{L})/u, (_m, c) => `de ${c.toUpperCase()}`)
                  : formatted;
                return (
                  <View style={styles.todayHeaderRow}>
                    <View style={styles.todayHeaderAvatar}>
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path d="M7.83984 1.84863V13.6887" stroke="#669AB7" strokeWidth="2" strokeLinecap="square" />
                        <Path d="M13.7599 7.76855H1.91992" stroke="#669AB7" strokeWidth="2" strokeLinecap="square" />
                        <Path d="M12.0264 3.58301L3.6543 11.9551" stroke="#669AB7" strokeWidth="2" strokeLinecap="square" />
                        <Path d="M12.0264 11.9547L3.6543 3.58252" stroke="#669AB7" strokeWidth="2" strokeLinecap="square" />
                      </Svg>
                    </View>
                    <Text style={styles.todayDateLabel}>
                      {t('missoes.todayDateLabel', { date: capitalized })}
                    </Text>
                  </View>
                );
              })()}

              {/* Timeline — vertical rail anchoring today's missions, the
                  historical entry points, and the origin marker. The rail
                  itself is an absolute-positioned line; each row owns its
                  own marker so the rail visually threads them together. */}
              <View style={styles.timelineWrapper}>
                <View style={styles.timelineRail} pointerEvents="none" />

                {/* Today missions row — small black-square dot on the rail
                    + "Suas missões de hoje" heading + flat mission list. */}
                <View style={styles.timelineRow}>
                  <View style={styles.timelineMarker}>
                    <View style={styles.timelineDot} />
                  </View>
                  <View style={styles.timelineContent}>
                    <Text style={styles.todaySubheading}>{t('missoes.todayHeading')}</Text>
                    {MISSION_KEYS.map((key, idx) => {
                      const m = missoesData.missions.find((mm) => mm.key === key);
                      return (
                        <MissionRow
                          key={key}
                          missionKey={key}
                          completed={!!m?.completed}
                          isLast={idx === MISSION_KEYS.length - 1}
                        />
                      );
                    })}
                  </View>
                </View>

                {/* History entry points anchored to the same rail. Marker
                    rendered inside MissionHistoryRow already; we just nudge
                    each row's left padding so the marker lands on the rail. */}
                {(() => {
                  const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
                  const fmt = (d: Date) => {
                    const s = d.toLocaleDateString(locale, { day: '2-digit', month: 'long' });
                    return locale === 'pt-BR'
                      ? s.replace(/de (\p{L})/u, (_m, c) => `de ${c.toUpperCase()}`)
                      : s;
                  };
                  const iso = (d: Date) => d.toISOString().slice(0, 10);
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  const dayBefore = new Date();
                  dayBefore.setDate(dayBefore.getDate() - 2);
                  const yesterdayLabel = t('missoes.history.yesterdayDateLabel', {
                    date: fmt(yesterday),
                  });
                  return (
                    <View style={styles.timelineHistoryGroup}>
                      <MissionHistoryRow
                        variant="day"
                        label={yesterdayLabel}
                        date={iso(yesterday)}
                      />
                      <MissionHistoryRow
                        variant="day"
                        label={fmt(dayBefore)}
                        date={iso(dayBefore)}
                      />
                      <MissionHistoryRow variant="aggregate" labelKey="week" />
                      <MissionHistoryRow variant="aggregate" labelKey="month" />
                      <MissionHistoryRow variant="aggregate" labelKey="threeMonths" />
                      <MissionHistoryRow variant="aggregate" labelKey="sixMonths" />
                      <MissionHistoryRow variant="aggregate" labelKey="twelveMonths" isLast />
                    </View>
                  );
                })()}

                {/* Bottom origin marker — closes the rail. Pulls the
                    avatar + arrival date from the authed user internally. */}
                <JourneyOriginMarker />
              </View>

              {/* Tab-bar safe-area buffer. Sized to clear the bottom nav
                  without leaving a visible gap below the arrival marker —
                  the previous 120px value was a holdover from when this
                  page had a sticky CTA. */}
              <View style={{ height: 24 }} />
            </>
          ) : null}
        </Animated.ScrollView>

        {/* Page 1: Relógio */}
        <View key="relogio" style={{ flex: 1 }}>
          <SobrietyClockFull />
        </View>

        {/* Page 2: Notas */}
        <Animated.ScrollView 
          key="notas"
          showsVerticalScrollIndicator={false}
          stickyHeaderIndices={[1]}
          contentContainerStyle={styles.mainScrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#669AB7"
              colors={['#669AB7']}
            />
          }
        >
        {/* Sentiment Calendar */}
        <SentimentCalendar />

        {/* Filter Section - Sticky */}
        <View>
          <LinearGradient
            colors={['#F9F8F3', '#FFFFFF']}
            style={styles.filterSection}
          >
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterContainer}>
          <TouchableOpacity 
            style={[styles.filterChip, filters.author !== 'all' && styles.activeFilterChip]}
            onPress={handleAuthorFilterPress}
          >
            <Svg width="16" height="16" viewBox="0 0 25 25" fill="none">
              <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 15.8462C8.61731 15.8462 5.31445 16.431 5.31445 18.7729C5.31445 21.1148 8.59636 21.7205 12.4849 21.7205C16.3525 21.7205 19.6545 21.1348 19.6545 18.7938C19.6545 16.4529 16.3735 15.8462 12.4849 15.8462Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 12.5059C15.023 12.5059 17.0802 10.4478 17.0802 7.90969C17.0802 5.3716 15.023 3.31445 12.4849 3.31445C9.94682 3.31445 7.8887 5.3716 7.8887 7.90969C7.88015 10.4392 9.92396 12.4973 12.4525 12.5059H12.4849Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={[styles.filterText, filters.author !== 'all' && styles.activeFilterText]}>
              {filters.author === 'all' ? t('diary.filters.author') : filters.author === 'user' ? t('diary.filters.you') : t('diary.filters.bill')}
            </Text>
            {filters.author !== 'all' ? (
              <TouchableOpacity onPress={() => setFilters(prev => ({ ...prev, author: 'all' }))}>
                <Svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                  <Path d="M3.16992 9.32843L8.82678 3.67158" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M3.16992 3.67157L8.82678 9.32842" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </TouchableOpacity>
            ) : (
              <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                <Path d="M12 6.5L8 10.5L4 6.5" stroke="#3D3C38" strokeWidth="1.5" strokeLinecap="square" />
              </Svg>
            )}
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.filterChip, filters.period !== 'all' && styles.activeFilterChip]}
            onPress={handlePeriodFilterPress}
          >
            <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
              <Path d="M10.1016 2.96155V4.94509" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M5.28711 2.96155V4.94509" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path fillRule="evenodd" clipRule="evenodd" d="M10.4547 3.87622L4.93844 3.87677C3.31809 3.87677 2.30664 4.77908 2.30664 6.43759V11.4304C2.30664 13.1152 3.31809 14.0384 4.93844 14.0384L10.45 14.0378C12.0751 14.0378 13.0817 13.1307 13.0817 11.4716V6.43705C13.0865 4.77853 12.0798 3.87622 10.4547 3.87622Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
            <Text style={[styles.filterText, filters.period !== 'all' && styles.activeFilterText]}>
              {filters.period === 'all' ? t('diary.filters.period') : 
               filters.period === '7days' ? t('diary.filters.last7days') : 
               filters.period === '15days' ? t('diary.filters.last15days') : t('diary.filters.last30days')}
            </Text>
            {filters.period !== 'all' ? (
              <TouchableOpacity onPress={() => setFilters(prev => ({ ...prev, period: 'all' }))}>
                <Svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                  <Path d="M3.16992 9.32843L8.82678 3.67158" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M3.16992 3.67157L8.82678 9.32842" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </TouchableOpacity>
            ) : (
              <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                <Path d="M12 6.5L8 10.5L4 6.5" stroke="#3D3C38" strokeWidth="1.5" strokeLinecap="square" />
              </Svg>
            )}
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.filterChip, filters.sentiment !== 'all' && styles.activeFilterChip]}
            onPress={handleSentimentFilterPress}
          >
            <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
              <Path d="M8 14.5C11.3137 14.5 14 11.8137 14 8.5C14 5.18629 11.3137 2.5 8 2.5C4.68629 2.5 2 5.18629 2 8.5C2 11.8137 4.68629 14.5 8 14.5Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M6.4 10.1C6.86 10.66 7.42 10.94 8 10.94C8.58 10.94 9.14 10.66 9.6 10.1" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M6.2 7.3V7.26M6.17 7.16C6.09 7.16 6.03 7.22 6.03 7.3C6.03 7.38 6.09 7.44 6.17 7.44C6.25 7.44 6.31 7.38 6.31 7.3C6.31 7.22 6.25 7.16 6.17 7.16Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M9.8 7.3V7.26M9.77 7.16C9.69 7.16 9.63 7.22 9.63 7.3C9.63 7.38 9.69 7.44 9.77 7.44C9.85 7.44 9.91 7.38 9.91 7.3C9.91 7.22 9.85 7.16 9.77 7.16Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={[styles.filterText, filters.sentiment !== 'all' && styles.activeFilterText]}>
              {filters.sentiment === 'all' ? t('diary.filters.sentiment') : (() => {
                const selectedSentiments = filters.sentiment.split(',').filter(Boolean);
                if (selectedSentiments.length === 1) {
                  return t(`diary.emotions.${selectedSentiments[0]}`) || t('diary.filters.sentiment');
                } else if (selectedSentiments.length > 1) {
                  return t('diary.filters.sentimentMultiple', { count: selectedSentiments.length.toString() });
                }
                return t('diary.filters.sentiment');
              })()}
            </Text>
            {filters.sentiment !== 'all' ? (
              <TouchableOpacity onPress={() => setFilters(prev => ({ ...prev, sentiment: 'all' }))}>
                <Svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                  <Path d="M3.16992 9.32843L8.82678 3.67158" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M3.16992 3.67157L8.82678 9.32842" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </TouchableOpacity>
            ) : (
              <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                <Path d="M12 6.5L8 10.5L4 6.5" stroke="#3D3C38" strokeWidth="1.5" strokeLinecap="square" />
              </Svg>
            )}
          </TouchableOpacity>
            </ScrollView>
          </LinearGradient>
        </View>

        {/* Notes Feed */}
        <View style={styles.notesContainer}>
          {isLoading || historicalCheckinsLoading ? (
            <>
              <SkeletonPost />
              <SkeletonPost />
              <SkeletonPost />
              <SkeletonPost />
              <SkeletonPost />
            </>
          ) : historicalCheckinsError ? (
            <View style={styles.emptyState}>
              <View style={styles.emptyStateContent}>
                <Text style={styles.emptyStateTitle}>Erro ao carregar check-ins</Text>
                <Text style={styles.emptyStateSubtitle}>{historicalCheckinsError}</Text>
              </View>
            </View>
          ) : filteredNotes.length > 0 ? (
            // P04 audit: FlatList not applied here. The notes list lives inside
            // an Animated.ScrollView managed by SwipeableTabs for horizontal
            // page swiping. Nesting FlatList inside Animated.ScrollView disables
            // FlatList's virtualization (RN VirtualizedList warning). Migrating
            // would require converting SwipeableTabs to Animated.FlatList — a
            // larger refactor. Notes per user are typically <100, so the
            // current RSS cost is acceptable. Revisit if median note count
            // exceeds 200 entries (track via P04b in the backlog).
            filteredNotes.map((note, index) => (
              <NoteCard key={note.id} note={note} isLast={index === filteredNotes.length - 1} />
            ))
          ) : (
            <View style={styles.emptyState}>
              <View style={styles.emptyIconBox}>
                <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <Path d="M2.91602 17.2136C2.91602 17.2136 3.32573 7.21321 14.2278 3.19575C23.5673 0.706593 12.2238 10.3889 12.2238 10.3889L7.55404 10.8475L9.72093 12.5627C7.59433 14.0575 5.4686 14.7964 3.34202 14.7767" fill="white" />
                  <Path d="M2.91602 17.2136C2.91602 17.2136 3.32573 7.21321 14.2278 3.19575C23.5673 0.706593 12.2238 10.3889 12.2238 10.3889L7.55404 10.8475L9.72093 12.5627C7.59433 14.0575 5.4686 14.7964 3.34202 14.7767" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M9.34766 17.2134H14.6894" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </View>
              <View style={styles.emptyTextGroup}>
                <Text style={styles.emptyDateText}>
                  {`${t('diary.today')}, ${new Date().toLocaleDateString(
                    language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US',
                    { day: 'numeric', month: 'long' }
                  )}`}
                </Text>
                <Text style={styles.emptyStateTitle}>{t('diary.emptyState.notesTitle')}</Text>
              </View>
              <TouchableOpacity
                style={styles.emptyStateCTA}
                onPress={() => router.push('/add-note')}
                activeOpacity={0.8}
              >
                <Text style={styles.emptyStateCTAText}>{t('diary.emptyState.notesButton')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </Animated.ScrollView>
      </SwipeableTabs>

      {/* Current stage sheet — opened from the greeting-row chip on Missões */}
      {missoesData?.stage?.number ? (
        <CurrentStageSheet
          visible={currentStageOpen}
          stageNumber={missoesData.stage.number}
          onDismiss={() => setCurrentStageOpen(false)}
        />
      ) : null}

      {/* Floating Add Button - Notas tab only, hidden when empty */}
      {activeTab === 'notas' && filteredNotes.length > 0 && (
      <TouchableOpacity style={styles.floatingButton} onPress={handleAddNote}>
        <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <Path d="M3.5 20.6561C3.5 20.6561 3.99166 8.65565 17.0741 3.8347C28.2815 0.847713 14.6693 12.4665 14.6693 12.4665L9.06563 13.0168L11.6659 15.075C9.11398 16.8688 6.5631 17.7555 4.0112 17.7318" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <Path opacity="0.4" d="M11.2168 20.6562H17.6269" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </Svg>
      </TouchableOpacity>
      )}

      {/* Floating Menu - Notas tab only */}
      {activeTab === 'notas' && floatingMenuVisible && (
        <>
          {/* Sentimento Button */}
          <Animated.View style={[styles.floatingMenuButton, styles.sentimentButton, animatedSentimentStyle]}>
            <TouchableOpacity 
              style={styles.floatingMenuButtonContent}
              onPress={() => {
                handleAddNote();
                router.push({
                  pathname: '/diario-sentimento',
                  params: { returnTo: 'diary' }
                });
              }}
            >
              <Svg width="20" height="21" viewBox="0 0 20 21" fill="none">
                <Path d="M10.418 17.75C14.5604 17.75 17.918 14.3916 17.918 10.25C17.918 6.10757 14.5604 2.75 10.418 2.75C6.27554 2.75 2.91797 6.10757 2.91797 10.25C2.91797 14.3916 6.27554 17.75 10.418 17.75Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M8.01562 12.1577C8.60022 12.9182 9.46047 13.3925 10.4156 13.3925C11.3708 13.3925 12.231 12.9182 12.8156 12.1577" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M7.72461 7.75444V7.71148M7.68667 7.58398C7.58334 7.58398 7.49917 7.66815 7.50001 7.77148C7.50001 7.87482 7.58417 7.95898 7.68751 7.95898C7.79084 7.95898 7.87501 7.87482 7.87501 7.77148C7.87501 7.66732 7.79084 7.58398 7.68667 7.58398Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M13.1102 7.75444V7.71148M13.1046 7.58398C13.0013 7.58398 12.9171 7.66815 12.918 7.77148C12.918 7.87482 13.0021 7.95898 13.1055 7.95898C13.2088 7.95898 13.293 7.87482 13.293 7.77148C13.293 7.66732 13.2088 7.58398 13.1046 7.58398Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.floatingMenuButtonText}>{t('diary.buttons.sentiment')}</Text>
            </TouchableOpacity>
          </Animated.View>

          {/* Anotação Button */}
          <Animated.View style={[styles.floatingMenuButton, styles.noteButton, animatedNoteStyle]}>
            <TouchableOpacity 
              style={styles.floatingMenuButtonContent}
              onPress={() => {
                handleAddNote();
                router.push({
                  pathname: '/add-note',
                  params: { returnTo: 'diary' }
                });
              }}
            >
              <Svg width="20" height="21" viewBox="0 0 20 21" fill="none">
                <Path d="M2.91797 17.8386C2.91797 17.8386 3.32769 7.83821 14.2297 3.82075C23.5692 1.33159 12.2257 11.0139 12.2257 11.0139L7.55599 11.4725L9.72289 13.1877C7.59629 14.6825 5.47055 15.4214 3.34397 15.4017" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M9.34766 17.8384H14.6894" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.floatingMenuButtonText}>{t('diary.buttons.note')}</Text>
            </TouchableOpacity>
          </Animated.View>
        </>
      )}

      {/* Background overlay to close floating menu */}
      {activeTab === 'notas' && floatingMenuVisible && (
        <TouchableOpacity 
          style={styles.floatingMenuOverlay}
          onPress={handleAddNote}
          activeOpacity={1}
        />
      )}

      {/* Search Overlay - Full Screen */}
      <SearchOverlay
        visible={isSearchActive}
        query={filters.search}
        onChangeQuery={(text) => setFilters(prev => ({ ...prev, search: text }))}
        onSubmit={handleSearchSubmit}
        onCancel={handleSearchCancel}
        placeholder={t('diary.filters.search')}
        cancelLabel={t('diary.search.cancel')}
        recentSearches={recentSearches}
        onRecentSearchPress={handleRecentSearchPress}
      >
        <View style={styles.searchResultsSection}>
          <Text style={styles.searchResultsTitle}>
            {filteredNotes.length === 1
              ? t('diary.search.results', { count: filteredNotes.length.toString() })
              : t('diary.search.resultsPlural', { count: filteredNotes.length.toString() })}
          </Text>
          <View style={styles.searchResultsList}>
            {filteredNotes.map((note, index) => (
              <NoteCard key={note.id} note={note} isLast={index === filteredNotes.length - 1} />
            ))}
          </View>
        </View>
      </SearchOverlay>

      {/* Author Filter Drawer - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: authorFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={authorFilterVisible}
          onClose={() => setAuthorFilterVisible(false)}
          title={t('diary.filters.author')}
          onApply={handleApplyAuthorFilter}
          onClear={handleClearAuthorFilter}
          isApplyEnabled={tempAuthorFilter !== 'all'}
          type="author"
        >
          <AuthorFilterContent
            selectedAuthor={tempAuthorFilter}
            onAuthorChange={setTempAuthorFilter}
          />
        </FilterDrawer>
      </View>

      {/* Period Filter Drawer - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: periodFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={periodFilterVisible}
          onClose={() => setPeriodFilterVisible(false)}
          title={t('diary.filters.period')}
          onApply={handleApplyPeriodFilter}
          onClear={handleClearPeriodFilter}
          isApplyEnabled={tempPeriodFilter !== 'all'}
          type="period"
        >
          <PeriodFilterContent
            selectedPeriod={tempPeriodFilter}
            onPeriodChange={setTempPeriodFilter}
          />
        </FilterDrawer>
      </View>

      {/* Sentiment Filter Drawer - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: sentimentFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={sentimentFilterVisible}
          onClose={() => setSentimentFilterVisible(false)}
          title={t('diary.filters.sentiment')}
          onApply={handleApplySentimentFilter}
          onClear={handleClearSentimentFilter}
          isApplyEnabled={tempSentimentFilter !== 'all'}
          type="sentiment"
        >
          <SentimentFilterContent
            selectedSentiment={tempSentimentFilter}
            onSentimentChange={setTempSentimentFilter}
          />
        </FilterDrawer>
      </View>

      {/* Action Menu Bottom Sheet - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: actionMenuVisible ? 'auto' : 'none' }}>
        <ActionBottomSheet
          isVisible={actionMenuVisible}
          onClose={handleActionMenuClose}
          title={t('diary.actions.options')}
        >
        {selectedNote?.type === 'user' || selectedNote?.type === 'sentimento' ? (
          <>
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: 16,
                backgroundColor: '#F9F8F3',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: '#F0EEE4',
                gap: 16,
                marginBottom: 8,
              }} 
              onPress={handleEditNote}
            >
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.actionCardText}>{selectedNote?.type === 'sentimento' ? t('diary.actions.editEntry') : t('diary.actions.editNote')}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: 16,
                backgroundColor: '#F9F8F3',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: '#F0EEE4',
                gap: 16,
              }} 
              onPress={handleDeleteNote}
            >
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
              <Text style={styles.actionCardText}>{selectedNote?.type === 'sentimento' ? t('diary.actions.deleteEntry') : t('diary.actions.deleteNote')}</Text>
            </TouchableOpacity>
          </>
        ) : selectedNote?.type === 'checkin' ? (
          // Check-in notes are removable but not editable
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
            }} 
            onPress={handleDeleteNote}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>Remover check-in</Text>
          </TouchableOpacity>
        ) : selectedNote?.type === 'historical-checkin' ? (
          // Historical check-ins should show "Hide from diary" option
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
            }} 
            onPress={handleDeleteNote}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M2.5 10C2.5 10 6.5 4 10 4S17.5 10 17.5 10S13.5 16 10 16S2.5 10 2.5 10Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M3 3L17 17" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('diary.actions.hideFromDiary')}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
            }} 
            onPress={handleRemoveFavorite}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('diary.actions.removeFromDiary')}</Text>
          </TouchableOpacity>
        )}
        </ActionBottomSheet>
      </View>

      {/* Toast for feedback messages */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={() => setToastVisible(false)}
      />

      {/* Bottom Navigation */}
      <BillBottomNav />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  topHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 24,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 15,
    backgroundColor: '#F9F8F3',
  },
  mainScrollContent: {
    flexGrow: 1,
  },
  missoesScrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  missoesGreetingRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 4,
    gap: 12,
  },
  missoesGreetingTextWrap: {
    flex: 1,
    flexDirection: 'column',
  },
  missoesGreetingHello: {
    color: '#151515',
    fontFamily: 'Sentient',
    fontSize: 24,
    fontWeight: '400',
    lineHeight: 26,
  },
  missoesGreetingText: {
    color: '#151515',
    fontFamily: 'Sentient',
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 26,
  },
  missoesStageChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    height: 33,
    borderRadius: 17,
    backgroundColor: '#E6EBF0',
  },
  missoesStageChipText: {
    color: '#015480',
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 17,
  },
  missoesLoadingState: {
    paddingHorizontal: 16,
    paddingTop: 16,
    gap: 8,
  },
  // missoesErrorState / missoesErrorText / missoesRetryButton /
  // missoesRetryText were removed on 2026-04-30 in favour of the
  // shared <EmptyState /> component. See components/EmptyState.tsx.
  todayHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingTop: 8,
    paddingHorizontal: 24,
  },
  todayHeaderAvatar: {
    width: 32,
    height: 32,
    borderRadius: 20,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#DAD8CF',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
  },
  timelineWrapper: {
    marginTop: 8,
    // Figma 6313:26670/26671 use --Spacing-spacing-large (24px) on the
    // milestones sections; the previous 16px left a noticeable shift
    // versus the rest of the page (goal card, week strip).
    paddingHorizontal: 24,
    paddingBottom: 8,
    position: 'relative',
  },
  timelineRail: {
    position: 'absolute',
    // Marker column is 32px wide and starts at paddingHorizontal (24).
    // Center of the column → 24 + 16 = 40. Subtract half the rail width
    // (1.5/2) so the line sits visually centered behind the markers.
    left: 39.25,
    top: 16,
    bottom: 24,
    width: 1.5,
    backgroundColor: '#DAD8CF',
  },
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  timelineMarker: {
    width: 32,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#151515',
  },
  timelineContent: {
    flex: 1,
    minWidth: 0,
    paddingBottom: 16,
    gap: 12,
  },
  timelineHistoryGroup: {
    marginTop: 4,
  },
  todayDateLabel: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  todaySubheading: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  pillTabContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 8,
    backgroundColor: '#F9F8F3',
    gap: 12,
  },
  pillTabBar: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#F0EEE4',
    borderRadius: 32,
    padding: 4,
  },
  tabSearchButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  pillTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    borderRadius: 24,
    gap: 6,
  },
  pillTabActive: {
    backgroundColor: '#151515',
  },
  pillTabText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  pillTabTextActive: {
    color: '#FFFFFF',
  },
  avatarButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    textAlign: 'center',
  },
  notificationButton: {
    padding: 4,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF4444',
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  notificationBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontFamily: 'Inter_600SemiBold',
    lineHeight: 12,
  },
  filterSection: {
    paddingVertical: 16,
    paddingLeft: 24,
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 24,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
    gap: 4,
  },
  filterText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },
  activeFilterChip: {
    backgroundColor: '#F0EEE4',
    borderColor: '#151515',
  },
  activeFilterText: {
    color: '#151515',
    fontFamily: 'Inter_600SemiBold',
  },
  notesContainer: {
    flexGrow: 1,
    paddingBottom: 100,
    backgroundColor: '#FFFFFF',
  },
  noteCardContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
  },
  noteCardWithDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  noteAvatarColumn: {
    alignItems: 'center',
    flexShrink: 0,
  },
  billAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  userContent: {
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 8,
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  headerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  authorName: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    lineHeight: 19.6,
  },
  sentimentLabel: {
    color: '#015480',
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    lineHeight: 19.6,
  },
  timestamp: {
    color: '#757C85',
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 12,
  },
  noteMenuButton: {
    padding: 4,
  },
  noteContent: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    width: '100%',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 24,
  },
  emptyIconBox: {
    width: 40,
    height: 40,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyTextGroup: {
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  emptyDateText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  emptyStateTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
    width: 255,
  },
  emptyStateCTA: {
    backgroundColor: '#151515',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    height: 40,
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateCTAText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
    lineHeight: 19.6,
  },
  floatingButton: {
    position: 'absolute',
    bottom: 112,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  searchResultsSection: {
    paddingTop: 24,
  },
  searchResultsTitle: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#A19F97',
    marginBottom: 16,
  },
  searchResultsList: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  loadingState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    textAlign: 'center',
  },


  actionCardText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  floatingMenuButton: {
    position: 'absolute',
    right: 24,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    shadowColor: '#000',
    shadowOffset: {
      width: 4,
      height: 4,
    },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 1000,
  },
  floatingMenuButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 8,
  },
  sentimentButton: {
    bottom: 264, // 112 (main button bottom) + 56 (main button height) + 20 (spacing) + 76 (note button with padding)
  },
  noteButton: {
    bottom: 188, // 112 (main button bottom) + 56 (main button height) + 20 (spacing)
  },
  floatingMenuButtonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 19.6,
  },
  floatingMenuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 999,
  },

  // Appointment card styles (Figma reference)
  appointmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 24,
    marginTop: 24,
    marginBottom: 4,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
  },
  appointmentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  appointmentTextGroup: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 4,
  },
  appointmentTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  appointmentSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    lineHeight: 16,
  },
});